import { useLocation, useNavigate } from 'react-router-dom';

import { useMyPermissions } from '@/api/getMyPermissions';
import { pageGateFor } from '@/components/sidebar/pages';
import { setGameScope, setSandboxScope } from '@/lib/api-client';
import { storeGameSandbox } from '@/lib/utils';
import { holdsPermissions } from '@/models/permission';

/**
 * If the user is on an entity-specific page (e.g., player detail, snapshot detail),
 * navigate to the parent list to avoid 404 errors.
 */
export const getSafeNavigationPath = (
  currentPath: string,
  studioCode: string,
  gameCode: string,
  newSandbox: string,
): { path: string; preserveSearch: boolean } => {
  const basePath = `/studios/${studioCode}/${gameCode}/${newSandbox}`;

  const playerDetailPattern = /\/players\/\d+/;
  if (playerDetailPattern.test(currentPath)) {
    return { path: `${basePath}/players`, preserveSearch: false };
  }

  const snapshotDetailPattern = /\/store\/snapshot\/(?!new)[^/]+/;
  if (snapshotDetailPattern.test(currentPath)) {
    return { path: `${basePath}/store/snapshot/new`, preserveSearch: false };
  }

  const pathAfterSandbox = currentPath.replace(
    /^\/studios\/[^/]+\/[^/]+\/[^/]+/,
    basePath,
  );
  return { path: pathAfterSandbox, preserveSearch: true };
};

interface UseGameSwitchOptions {
  onBeforeSwitch?: () => void;
}

/**
 * Navigates to the sandboxes page for the new game.
 */
export const useGameSwitch = (options: UseGameSwitchOptions = {}) => {
  const navigate = useNavigate();

  const switchGame = (
    studioCode: string,
    currentGameCode: string,
    newGameCode: string,
  ) => {
    if (newGameCode === currentGameCode) return;

    options.onBeforeSwitch?.();

    navigate(`/studios/${studioCode}/${newGameCode}/sandboxes`);
  };

  return { switchGame };
};

interface UseSandboxSwitchOptions {
  onBeforeSwitch?: () => void;
}

/**
 * Handles API defaults, localStorage persistence, and safe navigation.
 * Preserves URL query parameters when staying on the same type of page.
 */
export const useSandboxSwitch = (options: UseSandboxSwitchOptions = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useMyPermissions();

  const switchSandbox = (
    studioCode: string,
    gameCode: string,
    currentSandbox: string,
    newSandbox: string,
  ) => {
    if (newSandbox === currentSandbox) return;

    options.onBeforeSwitch?.();

    // Before invalidating queries, so refetches use the new context.
    setGameScope(gameCode);
    setSandboxScope(newSandbox);

    storeGameSandbox(gameCode, newSandbox);

    const { path, preserveSearch } = getSafeNavigationPath(
      location.pathname,
      studioCode,
      gameCode,
      newSandbox,
    );
    const fullPath = preserveSearch ? `${path}${location.search}` : path;
    const base = `/studios/${studioCode}/${gameCode}/${newSandbox}`;
    const codes = pageGateFor(path.replace(`${base}/`, ''));
    const opens =
      !codes ||
      !data ||
      holdsPermissions(
        data.roles,
        { oneOf: codes },
        {
          studioCode,
          gameCode,
          sandboxCode: newSandbox,
        },
      );

    navigate(opens ? fullPath : base);
  };

  return { switchSandbox };
};
